import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getEscalatorBaseUrl } from '@atlassian/jira-servicedesk-incident-management-common/src/utils';
import {
	SUPPRESS_ALERT_CREATION,
	UNSUPPRESS_ALERT_CREATION,
} from '@atlassian/jira-servicedesk-system-incident-status/src/services/constants.tsx';
import type { ProjectId, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import { FETCH_RESPONDER_ALERTS_CONFIGURATION_ERROR } from '../../constants';
import type { State } from '../../types';
import messages from './messages';

type Args = {
	shouldSendResponderAlerts: boolean;
	cloudId: CloudId;
	projectId?: ProjectId;
	analyticsEvent?: UIAnalyticsEvent;
};

export const setResponderAlertsConfiguration =
	({ cloudId, projectId, shouldSendResponderAlerts, analyticsEvent }: Args): Action<State> =>
	async ({ getState, setState }) => {
		const { responderAlertsConfiguration } = getState();
		const { showFlag } = useFlagsService();
		const responderAlertConfigurationNew = shouldSendResponderAlerts
			? UNSUPPRESS_ALERT_CREATION
			: SUPPRESS_ALERT_CREATION;
		setState({
			responderAlertsConfiguration: {
				data: responderAlertConfigurationNew, // optimistic update
				isLoading: true,
				error: undefined,
			},
		});

		try {
			if (projectId == null) {
				throw new Error('projectId is not set');
			}

			await performPostRequest(
				`${getEscalatorBaseUrl(cloudId)}/project/${projectId}/alert-configuration`,
				{
					body: JSON.stringify({
						responderAlertCreationConfigurationType: responderAlertConfigurationNew,
					}),
				},
			);

			setState({
				responderAlertsConfiguration: {
					data: responderAlertConfigurationNew,
					isLoading: false,
					error: undefined,
				},
			});

			analyticsEvent &&
				fireTrackAnalytics(analyticsEvent, 'responderAlertsToggle changed', {
					value: shouldSendResponderAlerts,
				});

			if (shouldSendResponderAlerts) {
				showFlag({
					type: 'success',
					title: messages.alertsEnabledFlagHeading,
					description: messages.alertsEnabledFlagText,
					actions: [
						{
							onClick: () => {
								window.open(
									'https://support.atlassian.com/jira-service-management-cloud/docs/add-or-remove-the-responders-panel-from-your-service-project/',
									'_blank',
								);
							},
							content: messages.alertsEnabledLearnMoreLink,
						},
					],
				});
			} else {
				showFlag({
					type: 'success',
					title: messages.alertsDisabledFlagHeading,
					description: messages.alertsDisabledFlagText,
				});
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			if (responderAlertConfigurationNew === UNSUPPRESS_ALERT_CREATION) {
				showFlag({
					type: 'error',
					title: messages.alertsEnabledErrorTitle,
					description: messages.alertsToggleErrorDescription,
				});
			} else if (responderAlertConfigurationNew === SUPPRESS_ALERT_CREATION) {
				showFlag({
					type: 'error',
					title: messages.alertsDisabledErrorTitle,
					description: messages.alertsToggleErrorDescription,
				});
			}

			trackFetchErrors({
				event: analyticsEvent,
				error: e,
				id: 'setResponderAlertsConfiguration',
				sendToPrivacyUnsafeSplunk: true,
				packageName: 'jiraServicedeskIncidentManagementSettings',
			});

			setState({
				responderAlertsConfiguration: {
					...responderAlertsConfiguration,
					isLoading: false,
					error: { value: e.message, type: FETCH_RESPONDER_ALERTS_CONFIGURATION_ERROR },
				},
			});
		}
	};
